import React, { useEffect, useMemo } from 'react';
import { FormGroup, ControlLabel, Modal, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import PropTypes from 'prop-types';

import Spinner from '../../../../../../../../components/Loaders/Spinner';
import SelectVirtualized from '../../../../../../../../components/Select/Virtualized';
import Header from './Header';
import Services from './Services';
import Options from './Options';
import EmptyProviders from './EmptyProviders';

import * as importTypes from '../../reducer/constants';
import { checkingOptions, getProviderOptions, getProviderValue, isEmptyServices } from './helper';
import { getProvider } from '../../../../../helpers/getters';

const DropdownIndicator = () => (
  <div className="dropdown__indicator-loader">
    <Spinner />
  </div>
);

const Step1 = (props) => {
  const {
    onClose,
    list,
    support_copy_descriptions,
    dispatchImportState,
    errorProvider,
    isLoadingProvider,
    isLoading,
    providerId,
    checked,
    search,
    servicesCount,
  } = props;

  const { providers, config } = useSelector((state) => state.services);

  const selectOptions = useMemo(() => getProviderOptions(providers), [providers]);
  const selectValue = useMemo(() => getProviderValue(providers, providerId), [providers, providerId]);
  const showServices = useMemo(() => isEmptyServices(list), [list]);
  const showOptions = useMemo(() => checkingOptions(support_copy_descriptions), [support_copy_descriptions]);
  const provider = useMemo(() => getProvider(providerId), [providerId]);
  const servicesLimitReached = useMemo(
    () => {
      const servicesLength = servicesCount + (checked?.length || 0);
      return servicesLength > (config?.service_limit || 5000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checked],
  );

  const isContinueDisabled = !checked.length || !selectOptions.length || servicesLimitReached;

  const [loadingDebounce] = useDebounce(isLoading, 500);
  const [searchDebounce] = useDebounce(search, 500);

  useEffect(() => {
    dispatchImportState({ type: importTypes.IMPORT_CHANGE_SEARCH_SUCCESS });
  }, [dispatchImportState, loadingDebounce, searchDebounce]);

  const selectComponents = {
    IndicatorSeparator: null,
  };

  if (isLoadingProvider) {
    selectComponents.DropdownIndicator = DropdownIndicator;
  }

  const onChangeProvider = (selectedValue) => {
    const value = Number(selectedValue.value);
    if (providerId !== value) {
      dispatchImportState({ type: importTypes.IMPORT_CHANGE_PROVIDER_ID, value });
    }
  };

  const onChangeStep = () => {
    dispatchImportState({ type: importTypes.IMPORT_STEP_2, provider });
  };

  useEffect(() => {
    if (config.default_provider) {
      dispatchImportState({ type: importTypes.IMPORT_CHANGE_PROVIDER_ID, value: config.default_provider });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.default_provider])

  return (
    <div className="services-import__step">
      <Modal.Body>
        {servicesLimitReached ? (
          <div className="alert alert-danger">
            You can’t have more than
            {' '}
            {(config?.service_limit).toLocaleString('en-US')}
            {' '}
            services.
          </div>
        ) : null}

        {!selectOptions.length ? (
          <EmptyProviders />
        ) : (
          <>
            {config.access?.multiple_provider
              ? (
                <FormGroup className="form-group__import-provider">
                  <ControlLabel htmlFor="provider_id">Provider</ControlLabel>
                  <SelectVirtualized
                    autoFocus
                    components={selectComponents}
                    onChange={onChangeProvider}
                    noOptionsMessage={() => 'No providers'}
                    hideSelectedOptions={false}
                    name="provider_id"
                    isDisabled={isLoadingProvider}
                    value={selectValue}
                    options={selectOptions}
                    placeholder="Choose provider"
                  />
                </FormGroup>
              ) : null}
            {isLoadingProvider && !config.access?.multiple_provider ? <Spinner /> : null}
            {errorProvider ? <div className="alert alert-danger">{errorProvider}</div> : null}
            {showServices ? <Header {...props} dispatchImportState={dispatchImportState} /> : null}
            {showServices ? <Services {...props} provider={provider} dispatchImportState={dispatchImportState} /> : null}
          </>
        )}
      </Modal.Body>
      {showOptions ? <Options {...props} dispatchImportState={dispatchImportState} /> : null}
      <Modal.Footer className="custom__modal-footer services-import__footer">
        <Button onClick={onClose}>Close</Button>
        <div className="services-import__footer-next">
          {showServices && (
            <div className="counter">
              <span className="counter-numb">{checked.length}</span>
              <span className="counter-text">services selected</span>
            </div>
          )}
          <Button bsStyle="primary" disabled={isContinueDisabled} onClick={onChangeStep}>
            Continue
          </Button>
        </div>
      </Modal.Footer>
    </div>
  );
};

Step1.propTypes = {
  onClose: PropTypes.func,
  list: PropTypes.array,
  options: PropTypes.any,
  support_copy_descriptions: PropTypes.bool,
  dispatchImportState: PropTypes.func,
  errorProvider: PropTypes.any,
  isLoadingProvider: PropTypes.bool,
  isLoading: PropTypes.bool,
  providerId: PropTypes.any,
  checked: PropTypes.array,
  search: PropTypes.any,
  servicesCount: PropTypes.number,
};

export default Step1;
